import { useId, type ReactNode } from 'react';
import { IconCircle, IconCircleCheckFilled, IconSquare, IconSquareCheckFilled } from '@tabler/icons-react';

export interface PickerCardProps {
  selected: boolean;
  onSelect: () => void;
  /** The option, in a few words ("Glen Austin", "Just me"). */
  title: ReactNode;
  /** One short supporting line ("Leaves 09:00 · 3 seats free"). */
  description?: ReactNode;
  /** Optional picture on the left (icon, date tile, avatar). */
  media?: ReactNode;
  /** Optional content on the right (a pill, a price, a count). */
  trailing?: ReactNode;
  /** Several can be chosen (checkbox semantics) instead of one (radio semantics). */
  multi?: boolean;
  disabled?: boolean;
  /** Why it can't be chosen, shown under the description when disabled. */
  disabledNote?: ReactNode;
  /** Hide the radio / checkbox glyph (when `media` already makes the choice obvious). */
  hideIndicator?: boolean;
}

/**
 * A big tappable option (at least 60 px tall). Use instead of dropdowns for short lists. Put a
 * set of them inside <PickerGroup> so screen readers hear "radio button, 2 of 4, checked".
 */
export function PickerCard({ selected, onSelect, title, description, media, trailing, multi, disabled, disabledNote, hideIndicator }: PickerCardProps) {
  const Glyph = multi ? (selected ? IconSquareCheckFilled : IconSquare) : selected ? IconCircleCheckFilled : IconCircle;
  return (
    <button
      type="button"
      role={multi ? 'checkbox' : 'radio'}
      aria-checked={selected}
      className="rides-option rides-picker"
      disabled={disabled}
      onClick={onSelect}
    >
      {!hideIndicator && <span className="glyph" aria-hidden="true"><Glyph size={26} /></span>}
      {media}
      <span className="txt">
        <span className="t">{title}</span>
        {description && <span className="d">{description}</span>}
        {disabled && disabledNote && <span className="note">{disabledNote}</span>}
      </span>
      {trailing}
    </button>
  );
}

export interface PickerGroupProps {
  /** Visible question for the group ("Pick-up campus"). Rendered as a legend. */
  label?: ReactNode;
  /** Accessible name when the question is already the page title. */
  ariaLabel?: string;
  /** Checkbox group instead of radio group. */
  multi?: boolean;
  children: ReactNode;
}

/** Wraps PickerCards: radiogroup (or group for multi-select) with a visible or hidden label. */
export function PickerGroup({ label, ariaLabel, multi, children }: PickerGroupProps) {
  const id = useId();
  return (
    <div
      className="rides-pickergroup"
      role={multi ? 'group' : 'radiogroup'}
      aria-label={label ? undefined : ariaLabel}
      aria-labelledby={label ? id : undefined}
    >
      {label && <h2 id={id} className="rides-sectlabel" style={{ margin: '0 2px 2px' }}>{label}</h2>}
      {children}
    </div>
  );
}
